'use client';

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  DataGridPremium,
  GridColumnMenu,
  GridColumnMenuProps,
  GridRowSelectionModel,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { VOLUME_SELECT_OPTIONS } from 'src/components/constants';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useProspectSelection } from 'src/context/prospects';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import useEncounterColumns from 'src/hooks/table/useEncounterColumns';
import useGetReferralsV2 from 'src/hooks/referrals/useGetReferralsV2';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import useTableFilterSelections, { extractBaseId, resolveVolumeSortField } from 'src/hooks/filters/useTableFilterSelections';
import { LOCATION } from 'src/hooks/filters/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  NewNoVolumeOverlay,
  referralTableColumns,
  TableCheckbox,
} from '../components';

import {
  TableSettings,
  TableFooter,
  TableVolumeDrawer,
  EncountersToolbar,
} from 'src/components';
import { TableLoadingIndicator } from 'src/components/LoadingIndicator';
import { hasSitesOfCare } from 'src/utils/hasSitesOfCare/hasSitesOfCare';
import { useParams } from 'next/navigation';
import { useDebounce } from 'use-debounce';

const EMPTY_COLUMN_IDS: string[] = [];
const EMPTY_ROWS: any[] = [];

const OutboundReferralTable = () => {
  const hasLoaded = useRef(false);
  const prevFlagRef = useRef<boolean | undefined>(undefined);
  const previousColumnIdsRef = useRef<string[]>([]);
  const sortModelRef = useRef([]);
  const { id } = useParams<{ id: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const apiRef = useGridApiRef();
  const auth = useAuthorization();
  const { showEncountersTotals } = useFlags();
  const { entityDetails } = useProfile();
  const { name: profileName } = entityDetails || {};

  const {
    columnIds, // this is an array of volume ids [number, number, number]
    isTrending,
    date_range,
    setColumnId,
    isInitialized,
    apiVolumeFilterFormat: originalApiVolumeFilterFormat,
    editingColumnId, // Add this to detect when we're editing vs creating
  } = useTableFilterSelections(LOCATION.OUTBOUND_REFERRALS);

  // Map the mode values for outbound referrals:
  // - Treating -->  sends "treating" and Referring --> sends "receiving"
  const apiVolumeFilterFormat = useMemo(() => {
    if (!originalApiVolumeFilterFormat) return {};

    const result = { ...originalApiVolumeFilterFormat };

    // Map mode values appropriately for outbound referrals
    Object.keys(result).forEach((columnId) => {
      if (result[columnId]) {
        const currentMode = result[columnId].mode;
        result[columnId] = {
          ...result[columnId],
          // Map: treating -> treating, sending -> receiving
          mode: currentMode === 'sending' ? 'receiving' : currentMode,
        };
      }
    });

    return result;
  }, [originalApiVolumeFilterFormat]);

  // Use the hook with the specific table type and pass columnIds
  const { volumeColumns, optionColumns, createSecondaryTableColumns } =
    useEncounterColumns(LOCATION.OUTBOUND_REFERRALS, {
      setColumnId,
      isTrending,
      columnIds: isInitialized ? columnIds : EMPTY_COLUMN_IDS,
      profileName,
    });

  const {
    state: [, setNewProspects],
    selectedRows: [rowsAreSelected],
  } = useProspectSelection();

  const [search, setSearch] = useState('');
  const [debounceSearch] = useDebounce(search, 500);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [isExporting, setIsExporting] = useState(false);
  const [sort, setSort] = useState({
    sort: 'total_patients',
    sort_id: null,
    sort_field: null,
    order: 'desc',
    sortModel: [], // Add sortModel to track DataGrid state
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const currentYears = useMemo(() => {
    if (!date_range || !date_range?.gte || !date_range?.lte) return [];

    const years = [];
    const { gte, lte } = date_range;
    for (let year = Number(gte); year <= Number(lte); year++) {
      years.push(year);
    }

    return years;
  }, [date_range]);

  // toggle state to clear selected rows
  useEffect(() => {
    setSelectionModel(() => []);
  }, [rowsAreSelected]);

  // Create initial sort model based on columnIds
  const initialSortModel = useMemo(() => {
    if (hasLoaded.current === true) return;
    // Only set initial sort if we have columnIds and they're initialized
    if (columnIds?.length > 0 && isInitialized) {
      const firstColumnId = columnIds[0];

      return [
        { field: firstColumnId.toString(), sort: 'desc' as GridSortDirection },
      ];
    }
    return [{ field: 'total_patients', sort: 'desc' as GridSortDirection }]; // Default sort
  }, [columnIds, isInitialized]);

  // Handle when columnIds change after initial render
  useEffect(() => {
    const columnIdsChanged =
      JSON.stringify(previousColumnIdsRef.current) !==
      JSON.stringify(columnIds);

    // Recompute the default volume sort when the flag resolves/changes so ordering
    // stays aligned with the (encounters vs claims) value the cells now display.
    const flagChanged = prevFlagRef.current !== showEncountersTotals;
    prevFlagRef.current = showEncountersTotals;

    if (
      (!hasLoaded.current || flagChanged) &&
      columnIds?.length > 0 &&
      isInitialized
    ) {
      hasLoaded.current = true;
      const firstColumnId = columnIds[0];

      let sort_field = null;
      if (apiVolumeFilterFormat && firstColumnId) {
        // Use the full composite key to lookup in apiVolumeFilterFormat
        sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[firstColumnId],
          showEncountersTotals
        );
      }

      const newSortModel = [
        {
          field: firstColumnId.toString(),
          sort: 'desc' as GridSortDirection,
        },
      ];
      sortModelRef.current = newSortModel;
      setSort({
        sort: 'volume',
        sort_id: firstColumnId.toString(),
        sort_field: sort_field,
        order: 'desc',
        sortModel: newSortModel,
      });
    } else if (columnIdsChanged && editingColumnId && columnIds?.length > 0) {
      // We just applied an edit (editingColumnId is still set and columnIds changed)
      // Find the new column ID that replaced the editingColumnId
      let editedColumnId: string | null = null;

      // The edited column is the one that's different at the position where editingColumnId was
      const oldIndex = previousColumnIdsRef.current.indexOf(editingColumnId);
      if (oldIndex >= 0 && oldIndex < columnIds.length) {
        // The edited column should be at the same position but with a different ID
        const newColumnAtPosition = columnIds[oldIndex];
        if (newColumnAtPosition !== editingColumnId) {
          editedColumnId = newColumnAtPosition;
        }
      }

      // If we couldn't find it by position, look for any new column ID
      if (!editedColumnId) {
        editedColumnId =
          columnIds.find((id) => !previousColumnIdsRef.current.includes(id)) ||
          null;
      }

      if (editedColumnId) {
        // Set sort to the edited column
        let sort_field = null;
        if (apiVolumeFilterFormat && editedColumnId) {
          sort_field = resolveVolumeSortField(
            apiVolumeFilterFormat[editedColumnId],
            showEncountersTotals
          );
        }

        const editedSortModel = [
          {
            field: editedColumnId.toString(),
            sort: 'desc' as GridSortDirection,
          },
        ];
        sortModelRef.current = editedSortModel;
        setSort({
          sort: 'volume',
          sort_id: editedColumnId.toString(),
          sort_field: sort_field,
          order: 'desc',
          sortModel: editedSortModel,
        });

        // Apply sort to the DataGrid
        if (apiRef.current) {
          apiRef.current.setSortModel([
            {
              field: editedColumnId.toString(),
              sort: 'desc' as GridSortDirection,
            },
          ]);
        }
      }
    } else if (columnIds?.length === 0) {
      hasLoaded.current = null;
    }

    // Always update the previous columnIds reference
    if (columnIds) {
      previousColumnIdsRef.current = [...columnIds];
    }
  }, [
    columnIds,
    apiVolumeFilterFormat,
    showEncountersTotals,
    isInitialized,
    editingColumnId,
    apiRef,
  ]);

  // update selected rows when DataGrid selection model changes
  useEffect(() => {
    if (!apiRef.current?.getSelectedRows) return;
    setNewProspects(Array.from(apiRef.current.getSelectedRows().values()));

    return () => setNewProspects([]);
  }, [selectionModel]);

  // Fix sort state when columnIds change due to applySelections composite key updates
  useEffect(() => {
    // If we have volume columns and a current sort pointing to volume, but sortModel is wrong
    if (columnIds.length > 0 && sort.sort === 'volume' && sort.sort_id) {
      const currentSortBaseId = extractBaseId(sort.sort_id);

      // Check if current sort_id matches any columnId by base ID
      const matchingColumnId = columnIds.find(
        (id) => extractBaseId(id) === currentSortBaseId
      );

      if (matchingColumnId && matchingColumnId !== sort.sort_id) {
        // Update sort state to use the new composite key
        setSort((prev) => ({
          ...prev,
          sort_id: matchingColumnId,
          sortModel: [
            { field: matchingColumnId, sort: prev.order as GridSortDirection },
          ],
        }));
      }
    }
  }, [columnIds, sort.sort, sort.sort_id, sort.order]);

  // Memoize tableParams to prevent re-renders
  const tableParams = useMemo(
    () => ({
      sort: sort.sort,
      sort_id: sort.sort_id,
      sort_field: sort.sort_field,
      order: sort.order,
      // Don't include sortModel in API params
      q: debounceSearch || null,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    }),
    [
      sort.sort,
      sort.sort_id,
      sort.sort_field,
      sort.order,
      debounceSearch,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  const {
    data: referrals,
    isLoading: isLoadingReferral,
    isValidating: isFetchingReferral,
    exportReferrals,
  } = useGetReferralsV2({
    providerId: id,
    location: LOCATION.OUTBOUND_REFERRALS,
    tableParams: {
      ...tableParams,
      direction: 'outbound',
    },
    customApiVolumeFilterFormat: apiVolumeFilterFormat, // Pass our modified apiVolumeFilterFormat with mode='sending'
  });

  // CCN / Site ID columns are only relevant when the list contains a site of care.
  const hasSites = useMemo(
    () => hasSitesOfCare(referrals?.results),
    [referrals]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'outbound-referral',
    {
      name: true,
      specialty: true,
      total_patients: true,
      total_claims: true,
      npi: true,
      ccn: false,
      site_id: false,
      address: false,
      city: false,
      state: false,
      zip_code: false,
      phone: false,
      email: false,
    }
  );

  const handlePageChange = (e: any, newPage: number) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaginationModel({
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    });
  };

  // maintain column visibility state
  useEffect(() => {
    // Use a callback to ensure we're working with the latest state
    setColumnVisibilityModel((columnVisibilityState) => {
      const newVisibility = {
        ...columnVisibilityState,
        ...serializeColumnVisibility(),
      };

      // Force visibility for volume columns when they exist
      if (columnIds?.length > 0) {
        columnIds.forEach((id) => {
          newVisibility[id] = true;
        });
      }

      return newVisibility;
    });
  }, [
    volumeColumns,
    optionColumns,
    columnIds,
    currentYears,
    isTrending,
    hasSites,
    setColumnVisibilityModel,
  ]);

  const serializeColumnVisibility = useCallback(() => {
    // Process volume columns
    const volumeVisibility = volumeColumns.reduce((acc, col) => {
      if (col.field === 'crm') return acc;

      // If columnIds is empty, hide all volume columns
      if (!columnIds?.length) {
        acc[col.field] = false;
        return acc;
      }

      // Show volume columns only if their field matches an ID in columnIds
      const isVisible = columnIds.includes(col.field);
      acc[col.field] = isVisible;
      return acc;
    }, {});

    // Process option (trending) columns
    const optionVisibility = optionColumns.reduce((acc, col) => {
      const [, year] = col.field.split('_');
      const isValidYear = currentYears.includes(Number(year));
      const isInOptions = VOLUME_SELECT_OPTIONS?.some((o) => o.value === year);

      const isVisible =
        isInOptions && isValidYear && isTrending && columnIds?.length > 0;
      acc[col.field] = isVisible;
      return acc;
    }, {});

    // Combine all visibility settings
    const result = {
      ...volumeVisibility,
      ...optionVisibility,
      ccn: hasSites,
      site_id: hasSites,
    };

    return result;
  }, [
    volumeColumns,
    optionColumns,
    currentYears,
    columnIds,
    isTrending,
    hasSites,
  ]);

  const handleExportList = async (type: string) => {
    const visibleColumns = apiRef.current
      .getVisibleColumns()
      .map((col) => col.field)
      .concat(['volume', 'patients', 'claims'])
      .filter((col) => col.indexOf('__') === -1);

    setIsExporting(true);
    const fileName = `${entityDetails?.name}_OutboundReferrals`;

    try {
      await exportReferrals({
        fileFormat: type,
        columns: visibleColumns,
        fileName,
      });
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: LOCATION.OUTBOUND_REFERRALS,
        source: `Referral Table`,
        columns: visibleColumns,
        count: referrals?.results?.length,
      });
    } catch (err: any) {
      log.exception(`Error exporting Referral table: ${err}`);

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSortChange = useCallback(
    (model) => {
      // Early return if model hasn't changed
      if (JSON.stringify(model) === JSON.stringify(sortModelRef.current))
        return;
      sortModelRef.current = model;

      const { field, sort: sortDirection } = model?.[0] || {};
      // Check if field is a volume column by matching base ID (not exact composite key)
      const isVolumeColumn =
        field &&
        columnIds.some((id) => {
          const fieldBaseId = extractBaseId(field);
          const columnBaseId = extractBaseId(id);
          return fieldBaseId === columnBaseId;
        });

      // Helper to get volume sort field type
      const getVolumeSortField = (field) => {
        if (!field || !apiVolumeFilterFormat) return null;
        const baseId = extractBaseId(field);
        const compositeKey = Object.keys(apiVolumeFilterFormat).find(
          (key) => extractBaseId(key) === baseId
        );
        return resolveVolumeSortField(
          apiVolumeFilterFormat[compositeKey],
          showEncountersTotals
        );
      };

      // Determine sort parameters
      let sortValue, sort_field, sort_id;
      if (model?.length && isVolumeColumn) {
        sortValue = 'volume';
        sort_field = getVolumeSortField(field);
        sort_id = field; // Use the full composite key as sort_id
      } else if (!model?.length && columnIds.length > 0) {
        // Fallback to first volume column when model is empty
        sortValue = 'volume';
        sort_field = resolveVolumeSortField(
          apiVolumeFilterFormat[columnIds[0]],
          showEncountersTotals
        );
        sort_id = columnIds[0]; // Use the full composite key as sort_id
      } else {
        sortValue = field || 'total_patients';
        sort_field = null;
        sort_id = null;
      }

      setSort({
        sort: sortValue,
        sort_id: sort_id, // Use the full composite key for volume columns
        sort_field,
        order: sortDirection || 'desc',
        sortModel: model,
      });
    },
    [columnIds, apiVolumeFilterFormat, showEncountersTotals]
  );

  const handleSearch = (value) => {
    setSearch(value);
    // ensure we are on the 1st page for searches
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const secondaryTableColumns = useMemo(() => {
    return createSecondaryTableColumns(LOCATION.OUTBOUND_REFERRALS);
  }, [createSecondaryTableColumns]);

  // Get only volume, option, and empty column fields for pinning to the right
  const rightPinnedFields = useMemo(() => {
    // Create a map of volume columns by field for quick lookup
    const volumeColumnsMap = volumeColumns.reduce((acc, col) => {
      acc[col.field] = col;
      return acc;
    }, {});

    // Get volume column fields in the exact order of columnIds
    // Use base ID matching to handle composite key changes gracefully
    const volumeFields =
      columnIds && columnIds.length > 0
        ? columnIds
            .map((id) => id.toString())
            .filter((id) => {
              // First try exact match
              if (volumeColumnsMap[id]) return true;

              // If no exact match, try base ID match to handle key transitions
              const baseId = extractBaseId(id);
              return Object.keys(volumeColumnsMap).some(
                (key) => extractBaseId(key) === baseId
              );
            })
            .map((id) => {
              // Return the actual field that exists in volumeColumnsMap
              if (volumeColumnsMap[id]) return id;

              // Find matching base ID
              const baseId = extractBaseId(id);
              const matchingKey = Object.keys(volumeColumnsMap).find(
                (key) => extractBaseId(key) === baseId
              );
              return matchingKey || id;
            })
        : [];

    // Get option column fields (trend columns)
    const optionFields = optionColumns
      .filter(() => isTrending && columnIds.length > 0)
      .map((col) => col.field);

    // Add the empty drawer column
    const drawerField = ['volume_drawer'];

    // Combine all fields that should be pinned to the right
    // This matches the exact order in secondaryTableColumns
    return [...volumeFields, ...optionFields, ...drawerField];
  }, [
    volumeColumns,
    optionColumns,
    columnIds,
    isTrending,
    apiVolumeFilterFormat,
  ]);

  const combinedColumns = useMemo(() => {
    // Add proper sortingOrder to regular columns and remove custom renderHeader to fix sorting
    const configuredReferralColumns = referralTableColumns.map((col) => ({
      ...col,
      sortingOrder: ['asc', 'desc'] as any, // Regular columns: remove null state to prevent third click reset
      renderHeader: undefined, // Remove custom header to restore default DataGrid sorting behavior
    }));

    // Reverse the order of columns to help with pinning
    return [...secondaryTableColumns, ...configuredReferralColumns];
  }, [secondaryTableColumns, referralTableColumns]);

  const userCanExport = auth.hasPermission('export.old_referrals.all');

  return (
    <div className="flex flex-col p-0 bottom-0 h-full w-full min-w-0 overflow-hidden">
      <div className="h-192 min-w-0">
        <div className="relative border border-neutral-200 rounded-lg flex flex-col p-0 bottom-0 h-full w-full min-w-0 overflow-hidden bg-white">
          <div className="px-4 py-6 flex flex-col border-b border-neutral-200 bg-white ">
            <div className="w-full flex items-center font-semibold text-2xl leading-tight text-teal-700">
              Outbound Referrals
            </div>
            <div className="w-full flex items-center gap-1 text-sm text-neutral-600 leading-tight ">
              Providers were sent referrals by
              <span className="text-primary font-semibold">{profileName}</span>
            </div>
          </div>

          <div className="w-full h-full overflow-hidden flex flex-col min-w-0">
            <DataGridPremium
              disableVirtualization
              apiRef={apiRef}
              rows={referrals?.results || EMPTY_ROWS}
              rowCount={referrals?.count || 0}
              columns={combinedColumns}
              hideFooter={true}
              checkboxSelection
              disableColumnMenu
              disableRowSelectionOnClick
              pinnedColumns={{ right: rightPinnedFields }}
              sx={{
                // Base table styling
                border: 'none',
                borderRadius: 0,
                overflow: 'hidden',
                height: '100%',
                flex: 1,
                // Fix for pinned columns alignment
                '& .MuiDataGrid-columnHeaders': {
                  position: 'relative', // Ensure headers have a positioning context
                  paddingRight: '0px',
                },
                // Pinned column headers (v7: native sticky, no position overrides)
                '& .MuiDataGrid-columnHeader--pinnedRight': {
                  background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
                },
                // Pinned cells (v7: native sticky, no position overrides)
                '& .MuiDataGrid-cell--pinnedRight': {
                  background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                  overflowX: 'auto', // Allow horizontal scrolling
                  minHeight: '300px', // Ensure minimum height even when empty
                },
                // Empty state styling
                '& .MuiDataGrid-overlay': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },

                // Primary columns hover effect
                '& .MuiDataGrid-row:hover, & .Mui-hovered': {
                  backgroundColor: 'rgba(204, 204, 194, 0.13)',
                  '&.Mui-hovered': {
                    backgroundColor: 'rgba(204, 204, 194, 0.13)',
                  },
                },

                // Secondary columns - base styling
                '& .secondary-column-header, & .secondary-column-cell': {
                  background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
                  borderRight: '#00C6A9 1px solid',
                },

                // Secondary column headers specific styling
                '& .secondary-column-header': {
                  // borderLeft: columnIds.length === 0 ? '#00C6A9 1px solid' : 'none',
                  borderBottom: '#00C6A9 2px solid !important', // Restored 2px with !important to ensure visibility
                  boxShadow: 'none',
                },

                // Override any conflicting styles from MUI
                '& .MuiDataGrid-columnHeaders .secondary-column-header, & .MuiDataGrid-columnHeadersInner .secondary-column-header':
                  {
                    borderBottom: '#00C6A9 2px solid !important', // Ensure this is not overridden
                  },

                // Secondary column cells specific styling
                '& .secondary-column-cell': {
                  borderBottom: '#00C6A9 1px solid',
                  borderLeft:
                    columnIds.length === 0 ? '#00C6A9 1px solid' : 'none',
                },

                // First column in secondary section gets left border
                '& .secondary-first-column-header, & .secondary-first-column-cell':
                  {
                    borderLeft:
                      columnIds.length > 0 ? '#00C6A9 1px solid' : 'none',
                  },

                // Add left border to the first pinned column header and cell
                '& .MuiDataGrid-columnHeader--pinnedRight:first-of-type, & .MuiDataGrid-cell--pinnedRight:first-of-type':
                  {
                    borderLeft: '#00C6A9 1px solid',
                  },

                // Column separators in secondary section
                '& .secondary-column-header .MuiDataGrid-columnSeparator': {
                  color: '#00C6A9',
                },

                // Hover effect for secondary columns
                '& .MuiDataGrid-row:hover .secondary-column-cell, & .Mui-hovered .secondary-column-cell':
                  {
                    backgroundColor: '#D6F6F1',
                  },
                // Hover effect for pinned cells
                '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .Mui-hovered .MuiDataGrid-cell--pinnedRight':
                  {
                    backgroundColor: '#D6F6F1',
                  },
              }}
              initialState={{
                columns: { columnVisibilityModel },
                pagination: { paginationModel: { page: 0, pageSize: 50 } },
                sorting: { sortModel: initialSortModel },
              }}
              slots={{
                toolbar: EncountersToolbar,
                baseCheckbox: TableCheckbox,
                columnMenu: CustomColumnMenu,
                noRowsOverlay: () => (
                  <NewNoVolumeOverlay
                    columnIds={columnIds
                      .map((id) => {
                        const baseId = id.split('_')[0];
                        return parseInt(baseId, 10);
                      })
                      .filter((id) => !isNaN(id))}
                  />
                ),
                ...TableSettings.slots,
              }}
              slotProps={{
                columnsManagement: {
                  disableShowHideToggle: true,
                },
                toolbar: {
                  location: LOCATION.OUTBOUND_REFERRALS,
                  onSearch: handleSearch,
                  // Export button props
                  onExport: handleExportList,
                  isExporting: isExporting,
                  // Column button props
                  columns: referralTableColumns,
                  columnVisibilityModel: columnVisibilityModel,
                  setColumnVisibility: setColumnVisibilityModel,
                  // Feature flags
                  showExportButton: userCanExport,
                  showAddToListButton: true,
                },
              }}
              // Pagination
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              // Sorting
              sortingMode="server"
              sortModel={sort.sortModel || []}
              onSortModelChange={handleSortChange}
              // Column visibility
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              // Rows
              getRowHeight={({ densityFactor }) => densityFactor * 80}
              onRowSelectionModelChange={setSelectionModel}
              rowSelectionModel={selectionModel}
              getRowId={(row) => row.provider_id}
            />
          </div>
          <TableFooter
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            rowCount={referrals?.count || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
          {(isLoadingReferral || isFetchingReferral) && (
            <TableLoadingIndicator top="255px" />
          )}
        </div>
      </div>
      <TableVolumeDrawer
        location={LOCATION.OUTBOUND_REFERRALS}
        showBilling
        showTrending
        showReferringTreating
        defaultMode="sending"
      />
    </div>
  );
};

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuFilterItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
};

export default OutboundReferralTable;
